// redux/slices/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  loginUser as loginUserAPI,
  registerUser as registerUserAPI,
  isAuthenticated,
  getUserData,
  getUserFromToken,
  logoutUser as logoutUserAPI,
} from "../../services/authService";

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await loginUserAPI(credentials);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for registration
export const registerUser = createAsyncThunk(
  "auth/register",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await registerUserAPI(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Check if user is already authenticated on app start
export const checkAuthStatus = createAsyncThunk(
  "auth/checkStatus",
  async (_, { rejectWithValue }) => {
    try {
      if (isAuthenticated()) {
        // Try to get user from cookie first, then from token
        const user = getUserData() || getUserFromToken();
        return { user };
      } else {
        throw new Error("Not authenticated");
      }
    } catch (error) {
      return rejectWithValue("Not authenticated");
    }
  }
);

const initialState = {
  isLoggedIn: false,
  user: null,
  streakDays: 5,
  loading: false,
  error: null,
  isInitialized: false, // To track if auth check is complete
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      logoutUserAPI(); // This handles cookie removal and redirect
      state.isLoggedIn = false;
      state.user = null;
      state.error = null;
    },
    updateStreak: (state, action) => {
      state.streakDays = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoggedIn = true;
        state.user = action.payload.user || getUserData() || getUserFromToken();
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isLoggedIn = false;
        state.user = null;
      })
      // Register cases
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoggedIn = true;
        state.user = action.payload.user || getUserData() || getUserFromToken();
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isLoggedIn = false;
        state.user = null;
      })
      // Check auth status cases
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoggedIn = true;
        state.user = action.payload.user;
        state.isInitialized = true;
      })
      .addCase(checkAuthStatus.rejected, (state) => {
        state.isLoggedIn = false;
        state.user = null;
        state.isInitialized = true;
      });
  },
});

export const { logout, updateStreak, clearError } = authSlice.actions;
export default authSlice.reducer;
